import React, { useState } from 'react'
import { Box, Card, Typography, useMediaQuery } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import useModals from 'src/Hooks/useModals'
import { ManufacturerModel } from '../../models'
import { useTheme } from '@mui/system'
import { useParams } from 'react-router-dom'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { formatToISO639_3 } from 'src/Libs/languages/functions/default'
import { ChargingLocationMapModal } from '../charging-location-map-modal'
import { MANUFACTURERS_WITHOUT_GPS } from '../../consts'

interface StyleProps {
  disabled: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, { disabled }) => ({
  manufacturerCard: {
    width: '100%',
    cursor: disabled ? 'default' : 'pointer',
    mb: 2,
    transition: 'border-color 0.2s',
    border: '1px solid transparent',
    '&:hover': {
      border: disabled ? '1px solid transparent' : `1px solid ${theme.palette.primary.main}`,
    },
    backgroundColor: disabled ? theme.palette.shades.shade03 : undefined,
  },
}))

interface Props {
  manufacturer: ManufacturerModel
  disabled?: boolean
  hasChargingLocation: boolean
}

/*
 * It is a manufacturer item shown in vehicle manufacturers dialog list.
 * After clicking on the item you should be navigated to the manufacturer site for login
 */
const ManufacturerItem = ({ manufacturer, disabled = false, hasChargingLocation }: Props): JSX.Element => {
  const { classes } = useStyles({ disabled })
  const { name, id, logoUrl, vinNeeded } = manufacturer
  const { installationId } = useParams<{ installationId: string }>()
  const { openManufacturerConnectionTermsDialog } = useModals()
  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const { selectedAppLanguage } = useCustomTranslation()
  const [isLocationModalOpen, setIsLocationModalOpen] = useState<boolean>(false)
  // for context. The gps coordinates are not needed for BMW, MINI vehicles. Therefore we skip the charging location modal for BMW and MINI.
  const isManufacturerWithoutGps = MANUFACTURERS_WITHOUT_GPS.includes(name)

  const onClickHandler = (): void => {
    setIsLocationModalOpen(!hasChargingLocation)
    if (hasChargingLocation || isManufacturerWithoutGps) {
      openManufacturerConnectionTermsDialog({
        authUrlPayload: { vendor: id, language: formatToISO639_3(selectedAppLanguage) },
        installationId: Number(installationId),
        vinNeeded,
      })
    }
  }
  const handleCloseLocationModal = (successful: boolean): void => {
    setIsLocationModalOpen(false)
    if (successful) {
      /* proceed to manufacturer connection terms dialog */
      openManufacturerConnectionTermsDialog({
        authUrlPayload: { vendor: id, language: formatToISO639_3(selectedAppLanguage) },
        installationId: Number(installationId),
        vinNeeded,
      })
    }
  }

  return (
    <>
      {isLocationModalOpen && !isManufacturerWithoutGps && (
        <ChargingLocationMapModal isModalOpen={isLocationModalOpen} onClose={handleCloseLocationModal} />
      )}
      <Card className={classes.manufacturerCard} onClick={disabled ? undefined : onClickHandler} sx={{ maxWidth: upMd ? 500 : '100%' }}>
        <Box sx={{ pt: 2, pl: 2, pr: 2 }} display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            variant="body1"
            sx={theme => ({
              color: disabled ? theme.palette.grey.grey03 : undefined,
            })}
          >
            {name}
          </Typography>
          <Box sx={{ width: theme => theme.spacing(14), alignItems: 'center', display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              src={logoUrl}
              height={theme.spacing(4)}
              alt="manufacturer-logo"
              sx={disabled ? { opacity: 0.3, filter: 'grayscale(1)' } : undefined}
            />
          </Box>
        </Box>
      </Card>
    </>
  )
}

export default ManufacturerItem
